"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import Link from "next/link";

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface Feature {
  id: string;
  title: string;
  description: string;
  media: Picture;
  showLink: string;
  newTab: boolean;
  url: string;
  text: string;
  features: string[];
  price?: number;
}

interface FeaturesProps {
  data: {
    heading: string;
    description: string;
    feature: Feature[];
    isLive: boolean;
    useH1Tag?: boolean;
  };
}

const FeatureItem = ({
  title,
  description,
  media,
  showLink,
  newTab,
  url,
  text,
  price,
}: Feature) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="flex flex-col group cursor-pointer relative"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      viewport={{ once: true, margin: "-10%" }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden mb-5 rounded-lg">
        <Image
          src={media?.data?.attributes?.url || "/placeholder-product.jpg"}
          alt={media?.data?.attributes?.alternativeText || title}
          fill
          className="object-cover transition-transform duration-700 group-hover:scale-110"
          sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
        />

        {/* Mobile & Desktop Add to Cart */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end pb-4 justify-center">
          <motion.button
            className="bg-white text-black px-6 py-3 rounded-full text-xs font-medium shadow-lg hover:bg-gray-50 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 flex items-center space-x-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>Add to Cart</span>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </motion.button>
        </div>

        {/* Product tag */}
        {price && (
          <div className="absolute top-4 left-4 bg-white px-3 py-1.5 rounded-full text-sm font-medium shadow-sm">
            ${price}
          </div>
        )}

        {/* Favorite icon */}
        <button
          className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors duration-200"
          aria-label="Close"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>
      </div>

      <div className="text-center px-2">
        <h3 className="text-sm font-medium text-gray-900 mb-2 uppercase tracking-wider">
          {title}
        </h3>

        <p className="text-xs text-gray-600 mb-3 font-light line-clamp-2 leading-relaxed">
          {description}
        </p>

        {/* Color variants */}
        <div className="flex justify-center space-x-2 mb-4">
          <div className="w-4 h-4 rounded-full bg-blue-900 border-2 border-white shadow-sm cursor-pointer"></div>
          <div className="w-4 h-4 rounded-full bg-blue-700 border-2 border-white shadow-sm cursor-pointer"></div>
          <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow-sm cursor-pointer"></div>
        </div>

        {showLink && (
          <Link
            href={url}
            target={newTab ? "_blank" : "_self"}
            rel={newTab ? "noopener noreferrer" : ""}
            className="inline-flex items-center text-xs font-medium text-gray-900 hover:text-black transition-colors duration-200 group/link"
          >
            {text}
            <svg
              className="w-3 h-3 ml-1 transition-transform duration-300 group-hover/link:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        )}
      </div>
    </motion.div>
  );
};

export default function Features_Layout1({ data }: FeaturesProps) {
  if (!data.isLive) return null;
  // console.log("Feature Data...", data);

  return (
    <section className="py-16 md:py-24 bg-[var(--secondary-lightest)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-16 md:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          {data.useH1Tag ? (
            <h1 className="text-3xl md:text-4xl  text-gray-900 mb-4">
              {data.heading}
            </h1>
          ) : (
            <h2 className="text-3xl md:text-4xl  text-gray-900 mb-4">
              {data.heading}
            </h2>
          )}

          <p className="text-gray-600 max-w-2xl mx-auto">{data.description}</p>
          {/* <div className="w-20 h-0.5 bg-gray-300 mx-auto mb-6"></div> */}
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {data.feature.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: "easeOut",
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <FeatureItem {...feature} />
            </motion.div>
          ))}
        </div>

        {/* Enhanced View All Button */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <button
            className="group relative inline-flex items-center px-8 py-4 
  rounded-full text-sm font-medium border border-[var(--btn-bg-2)] 
   transition-all duration-300 overflow-hidden text-[var(--btn-bg-2)] hover:text-[var(--btn-text-2)]"
          >
            {/* Animated background fill */}
            <span
              className="absolute inset-0 bg-[var(--btn-bg-2)] translate-x-[-100%] group-hover:translate-x-0 
    transition-transform duration-500 ease-in-out"
            />

            {/* Button text */}
            <span className="relative z-10  ">View All Products</span>

            {/* Arrow with animation */}
            <motion.span
              className="ml-3 relative z-10"
              whileHover={{ x: 4 }}
              transition={{ duration: 0.3 }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </motion.span>
          </button>
        </motion.div>
      </div>
    </section>
  );
}
